import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * AuthContext — ONLY manages Supabase Auth (session, sign in/out).
 * 
 * NO direct database queries happen here.
 * All data operations go through the API client (lib/api.js) → Cloudflare Worker.
 */

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    const loadingTimeout = setTimeout(() => {
      if (mounted.current && loading) {
        console.warn('Auth loading timeout, setting loading to false');
        setLoading(false);
      }
    }, 10000);

    // Get current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted.current) return;
      clearTimeout(loadingTimeout);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setProfileFromUser(session.user);
      }
      setLoading(false);
    }).catch((err) => {
      console.error('Error getting session:', err);
      clearTimeout(loadingTimeout);
      if (mounted.current) setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted.current) return;
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        setProfileFromUser(session.user);
        if (event === 'SIGNED_IN' && window.location.hash.includes('type=signup')) {
          window.location.href = '/dashboard';
        }
      } else {
        setProfile(null);
      }
      if (mounted.current) setLoading(false);
    });

    return () => {
      mounted.current = false;
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  // Build profile from auth user metadata (no DB call needed)
  const setProfileFromUser = (authUser) => {
    setProfile({
      full_name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'User',
      email: authUser.email
    });
  };

  const signUp = async (email, password, fullName) => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/dashboard`
      }
    });

    if (error) throw error;

    // Supabase silently returns a user with empty identities[] when the email is already registered
    // (to prevent email enumeration). We detect this and surface it as a specific case.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      return {
        success: false,
        emailAlreadyExists: true,
        message: 'This email is already registered.'
      };
    }

    if (data?.user && !data?.session) {
      return {
        success: true,
        confirmationRequired: true,
        isLocalhost,
        message: isLocalhost
          ? 'Email confirmation required. Check your inbox, or confirm the user in Supabase Dashboard > Authentication > Users.'
          : 'Please check your email to confirm your account'
      };
    }
    return { success: true, confirmationRequired: false };
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`
    });
    if (error) throw error;
    return { success: true };
  };

  const resendConfirmationEmail = async (email) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`
      }
    });
    if (error) throw error;
    return { success: true };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.error('Sign out error:', error);
    setUser(null);
    setSession(null);
    setProfile(null);
    return { success: true };
  };

  const getToken = () => session?.access_token;

  const value = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    resendConfirmationEmail,
    getToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
